import { useEffect } from 'react'

import {
  AlbumCollectionTypeEnum,
  albumCollectionByIdQuery,
  albumCollectionCreate,
  albumCollectionUpdate,
} from '@/entities/album-collections'
import { type AuthorPreview } from '@/entities/authors'
import { type UploadFileWithId, UploadPicture } from '@/features/upload-files'
import { hasFormChanged } from '@/shared/lib/form-utils'
import { showNotificationWarningFx } from '@/shared/lib/providers'
import { CopyButton, HidingSpin, SelfValidateInput } from '@/shared/ui'
import { PrimaryButton } from '@/shared/ui'
import { SelfValidateTextArea } from '@/shared/ui'
import { PlusOutlined } from '@ant-design/icons'
import { Button, Flex, Form, Select, Space, Spin } from 'antd'
import { useUnit } from 'effector-react'
import { useFormik } from 'formik'
import { z } from 'zod'
import { toFormikValidationSchema } from 'zod-formik-adapter'

import { MAX_FIELD_LENGTH } from '../lib/constants'
import { getAlbumCollectionRequestByFormValues } from '../lib/get-album-collection-by-form'
import {
  $albumCollectionCommonInformationFormAuthors,
  $albumCollectionCommonInformationFormCoauthors,
  $authorsAlbumCollectionCommonInformationFormSearch,
  $coauthorsAlbumCollectionCommonInformationFormSearch,
  $isAlbumCollectionCommonInformationFormDataPending,
  AlbumCollectionCommonInformationFormGate,
  albumCollectionCommonInformationFormAlbumCollectionDataChanged,
  authorsAlbumCollectionCommonInformationFormPageIncreased,
  authorsAlbumCollectionCommonInformationFormQuery,
  authorsAlbumCollectionCommonInformationFormSearchChanged,
  authorsAlbumCollectionCommonInformationFormSearchReset,
  coauthorsAlbumCollectionCommonInformationFormPageIncreased,
  coauthorsAlbumCollectionCommonInformationFormQuery,
  coauthorsAlbumCollectionCommonInformationFormSearchChanged,
  coauthorsAlbumCollectionCommonInformationFormSearchReset,
} from '../model/album-collection-form-common-information.model'

const albumCollectionSchema = z.object({
  name: z.string(),
  description: z.string(),
  authorId: z.string(),
  coauthorIds: z.array(z.string()).optional(),
  type: z.nativeEnum(AlbumCollectionTypeEnum),
  pictureFile: z.custom<UploadFileWithId>().nullable(),
})

export type AlbumCollectionFormCommonInformationModel = z.infer<
  typeof albumCollectionSchema
>

interface AlbumCollectionFormCommonInformationProps {
  onAuthorButtonClick: (callback: (id: string) => void) => void
  initialAuthor?: AuthorPreview
}

export const AlbumCollectionFormCommonInformation = ({
  onAuthorButtonClick,
  initialAuthor,
}: AlbumCollectionFormCommonInformationProps) => {
  // Form data
  const albumCollection = useUnit(albumCollectionByIdQuery)
  const albumCollectionQueryStatus = useUnit(albumCollectionByIdQuery.$status)
  const formDataPending = useUnit(
    $isAlbumCollectionCommonInformationFormDataPending,
  )
  const changeAlbumCollectionData = useUnit(
    albumCollectionCommonInformationFormAlbumCollectionDataChanged,
  )
  useEffect(() => {
    if (albumCollection.data) {
      changeAlbumCollectionData(albumCollection.data)
    }
  }, [albumCollection, changeAlbumCollectionData])

  // Authors
  const authors = useUnit(authorsAlbumCollectionCommonInformationFormQuery)
  const authorSearch = useUnit(
    $authorsAlbumCollectionCommonInformationFormSearch,
  )
  const changeAuthorsSearch = useUnit(
    authorsAlbumCollectionCommonInformationFormSearchChanged,
  )
  const resetAuthorsSearch = useUnit(
    authorsAlbumCollectionCommonInformationFormSearchReset,
  )
  const authorsPageIncreased = useUnit(
    authorsAlbumCollectionCommonInformationFormPageIncreased,
  )
  const authorsData = useUnit($albumCollectionCommonInformationFormAuthors)

  // Coauthors
  const coauthors = useUnit(coauthorsAlbumCollectionCommonInformationFormQuery)
  const coauthorSearch = useUnit(
    $coauthorsAlbumCollectionCommonInformationFormSearch,
  )
  const changeCoauthorsSearch = useUnit(
    coauthorsAlbumCollectionCommonInformationFormSearchChanged,
  )
  const resetCoauthorsSearch = useUnit(
    coauthorsAlbumCollectionCommonInformationFormSearchReset,
  )
  const coauthorsPageIncreased = useUnit(
    coauthorsAlbumCollectionCommonInformationFormPageIncreased,
  )
  const coauthorsData = useUnit($albumCollectionCommonInformationFormCoauthors)

  const createAlbumCollection = useUnit(albumCollectionCreate)
  const updateAlbumCollection = useUnit(albumCollectionUpdate)

  const submitting =
    createAlbumCollection.pending || updateAlbumCollection.pending

  const formik = useFormik<AlbumCollectionFormCommonInformationModel>({
    initialValues: albumCollection.data
      ? {
          name: albumCollection.data.name,
          description: albumCollection.data.description ?? '',
          authorId: albumCollection.data.authors.filter((a) => !a.isCoauthor)[0]
            .id,
          coauthorIds: albumCollection.data.authors
            .filter((a) => a.isCoauthor)
            .map((a) => a.id),
          type: albumCollection.data.type,
          pictureFile: albumCollection.data.picture
            ? ({
                id: albumCollection.data.picture.id,
                uid: albumCollection.data.picture.id,
                name: albumCollection.data.picture.name,
                status: 'done',
                url: albumCollection.data.picture.fullCdnUrl,
              } as UploadFileWithId)
            : null,
        }
      : {
          name: '',
          description: '',
          authorId: initialAuthor?.id || '',
          coauthorIds: [],
          type: AlbumCollectionTypeEnum.Music,
          pictureFile: null,
        },
    validationSchema: toFormikValidationSchema(albumCollectionSchema),

    onSubmit: (
      values: AlbumCollectionFormCommonInformationModel,
      { setSubmitting },
    ) => {
      if (!values.pictureFile || values.pictureFile.status !== 'done') {
        void showNotificationWarningFx({ message: 'Обложка еще не загружена' })
      } else {
        const albumCollectionSubmittedData =
          getAlbumCollectionRequestByFormValues(values)
        if (albumCollection.data) {
          void updateAlbumCollection.start({
            id: albumCollection.data.id,
            data: albumCollectionSubmittedData,
          })
        } else {
          void createAlbumCollection.start({
            data: albumCollectionSubmittedData,
          })
        }
      }
      setSubmitting(false)
    },
  })

  const { isValid, isValidating, isSubmitting, values } = formik
  useEffect(() => {
    if (!isValid && !isValidating && isSubmitting) {
      void showNotificationWarningFx({ message: 'Не все поля заполнены' })
    }
  }, [isSubmitting, isValid, isValidating])

  const onAuthorCreated = (id: string) =>
    void formik.setFieldValue('authorId', id)
  const onCoauthorCreated = (id: string) => {
    void formik.setFieldValue('coauthorIds', [
      ...(formik.values.coauthorIds ?? []),
      id,
    ])
  }

  const handleAuthorsSelectPopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement
    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 32) {
      authorsPageIncreased()
    }
  }

  const handleCoauthorsSelectPopupScroll = (
    e: React.UIEvent<HTMLDivElement>,
  ) => {
    const target = e.target as HTMLDivElement
    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 32) {
      coauthorsPageIncreased()
    }
  }

  const formControlButtonsDisabled =
    !hasFormChanged(formik.values, formik.initialValues) || submitting

  return (
    <>
      <AlbumCollectionCommonInformationFormGate initialAuthor={initialAuthor} />
      <HidingSpin
        spinning={
          albumCollectionQueryStatus !== 'initial' &&
          (formDataPending || (!albumCollection.data && !albumCollection.error))
        }
      >
        <div style={{ maxWidth: 600 }}>
          <Form.Item required layout="vertical" label="Название">
            <SelfValidateInput
              name="name"
              showCount
              maxLength={MAX_FIELD_LENGTH.name}
              value={formik.values.name}
              onChange={(value) => {
                void formik.setFieldValue('name', value)
              }}
            />
          </Form.Item>
          <Form.Item
            required
            label="Автор"
            style={{ marginBottom: '8px' }}
            layout="vertical"
            tooltip={
              albumCollection.data
                ? 'Автора можно указать только при создании выпуска'
                : null
            }
          >
            <Space style={{ width: '100%' }} direction="vertical">
              <Flex gap={4}>
                <Select
                  showSearch
                  disabled={
                    Boolean(albumCollection.data) || Boolean(initialAuthor)
                  }
                  style={{ width: '100%' }}
                  loading={authors.pending}
                  searchValue={authorSearch || ''}
                  optionFilterProp="label"
                  value={formik.values.authorId}
                  onChange={(value: string) => {
                    void formik.setFieldValue('authorId', value)
                    resetAuthorsSearch()
                  }}
                  onSearch={(value: string) => {
                    changeAuthorsSearch(value)
                  }}
                  notFoundContent={
                    authors.pending ? <Spin size="small" /> : null
                  }
                  onOpenChange={(open) => {
                    if (!open) {
                      resetAuthorsSearch()
                    }
                  }}
                  onPopupScroll={handleAuthorsSelectPopupScroll}
                >
                  {authorsData
                    .filter((el) => !formik.values.coauthorIds?.includes(el.id))
                    .map((el) => (
                      <Select.Option
                        key={el.id}
                        value={el.id}
                        label={el.nickname}
                      >
                        {el.nickname}
                      </Select.Option>
                    ))}
                </Select>
                <CopyButton
                  getText={() => {
                    const selected = authorsData.find(
                      (el) => el.id === values.authorId,
                    )
                    return selected?.nickname ?? null
                  }}
                />
              </Flex>
              {!albumCollection.data && !initialAuthor && (
                <Button
                  onClick={() => {
                    onAuthorButtonClick(onAuthorCreated)
                  }}
                  icon={<PlusOutlined />}
                >
                  Добавить
                </Button>
              )}
            </Space>
          </Form.Item>

          {formik.values.authorId && (
            <Form.Item label="Соавторы" layout="vertical">
              <Space style={{ width: '100%' }} direction="vertical">
                <Flex gap={4}>
                  <Select
                    mode="multiple"
                    showSearch
                    value={formik.values.coauthorIds}
                    searchValue={coauthorSearch || ''}
                    style={{ width: '100%' }}
                    loading={coauthors.pending}
                    optionFilterProp="label"
                    onChange={(value: string[]) => {
                      void formik.setFieldValue('coauthorIds', value)
                      resetCoauthorsSearch()
                    }}
                    onSearch={(value: string) => {
                      changeCoauthorsSearch(value)
                    }}
                    notFoundContent={
                      authors.pending ? <Spin size="small" /> : null
                    }
                    onOpenChange={(open) => {
                      if (!open) {
                        resetCoauthorsSearch()
                      }
                    }}
                    onPopupScroll={handleCoauthorsSelectPopupScroll}
                  >
                    {coauthorsData
                      .filter((el) => el.id !== formik.values.authorId)
                      .map((el) => (
                        <Select.Option
                          key={el.id}
                          value={el.id}
                          label={el.nickname}
                        >
                          {el.nickname}
                        </Select.Option>
                      ))}
                  </Select>
                  <CopyButton
                    getText={() => {
                      const ids = values.coauthorIds
                      if (!ids || ids.length === 0) return null
                      const selected = ids
                        .map(
                          (id) =>
                            coauthorsData.find((el) => el.id === id)?.nickname,
                        )
                        .filter((name): name is string => Boolean(name))
                      if (selected.length === 0) return null
                      return selected.join(', ')
                    }}
                  />
                </Flex>
                <Button
                  onClick={() => {
                    onAuthorButtonClick(onCoauthorCreated)
                  }}
                  icon={<PlusOutlined />}
                >
                  Добавить
                </Button>
              </Space>
            </Form.Item>
          )}

          <Form.Item required label="Описание" layout="vertical">
            <SelfValidateTextArea
              name="description"
              showCount
              maxLength={MAX_FIELD_LENGTH.description}
              value={formik.values.description ?? ''}
              onChange={(value) => {
                void formik.setFieldValue('description', value)
              }}
            />
          </Form.Item>

          <Form.Item required layout="vertical" label="Обложка">
            <UploadPicture
              value={formik.values.pictureFile ?? undefined}
              onChange={(fileList) => {
                const file = fileList[0] ?? null
                void formik.setFieldValue('pictureFile', file)
              }}
              onReset={() => {
                void formik.setFieldValue('pictureFile', null)
              }}
            />
          </Form.Item>

          <Space>
            {!albumCollection.data && (
              <Button
                disabled={formControlButtonsDisabled}
                onClick={() => formik.resetForm()}
              >
                Очистить
              </Button>
            )}
            <PrimaryButton
              disabled={formControlButtonsDisabled}
              loading={submitting}
              onClick={() => {
                formik.handleSubmit()
              }}
            >
              {albumCollection.data ? 'Сохранить' : 'Продолжить'}
            </PrimaryButton>
          </Space>
        </div>
      </HidingSpin>
    </>
  )
}
